import React, { useEffect, useState, useMemo } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { useAuth } from '@/lib/auth';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatINR, formatGrams, formatDateTime, toISODateInput, nowISTDatetimeLocal, istDatetimeLocalToISO } from '@/lib/format';
import { Plus, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, Trash2, Printer, ArrowUp, ArrowDown, Search, CalendarDays, ArrowLeft, ArrowRight, Info, Layers, List, ChevronRight } from 'lucide-react';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';
import MemoLink from '@/components/phase7/MemoLink';
import ReturnMemoLink from '@/components/phase7/ReturnMemoLink';
import ReturnDialog from '@/components/phase7/ReturnDialog';
import { VendorFormDialog } from '@/components/phase10/VendorFormDialog';
import { PurityFormDialog } from '@/components/phase10/PurityFormDialog';
import { GoldIssueDialog } from '@/components/phase14/GoldIssueDialog';
import { DatePicker } from '@/components/ui/date-picker';
import PdfDownloadDialog from '@/components/phase11/PdfDownloadDialog';

// ============================================================================
// Gold purity view (999 ↔ 995) for the Loose Gold Ledger.
// Every gold figure is stored in the canonical 999 basis. Users may prefer to
// read the ledger in 995 grams. Per spec, 999 = 100% pure and 995 = 99.5%, so
// to hold the same pure gold the 995 figure is slightly higher:
//     grams_995 = grams_999 / 0.995
// Choice is remembered in localStorage.
// ============================================================================
const LOOSE_GOLD_PURITY_KEY = 'gold.looseLedger.purityView';

// Wave — Rate / Value column toggles for the Loose Gold Ledger. Mirrors the
// Customer Gold Ledger Report: an operator who is permitted to see values may
// still hide the Rate and/or Value columns (e.g. to focus on grams movement or
// to share the screen without exposing amounts). The choice is remembered per
// browser and the exported PDF mirrors the on-screen selection.
const LOOSE_GOLD_SHOW_RATE_KEY = 'gold.looseLedger.showRate';
const LOOSE_GOLD_SHOW_VALUE_KEY = 'gold.looseLedger.showValue';
// Detailed vs Merged ledger view. Merged groups same-day entries for the same
// party (issues → goldsmith, receipts → vendor/customer/goldsmith return).
const LOOSE_GOLD_VIEW_MODE_KEY = 'gold.looseLedger.viewMode';

function readLooseColPref(key) {
  try {
    const v = localStorage.getItem(key);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch { /* noop */ }
  return true; // default ON
}

function readLoosePurity() {
  try {
    return localStorage.getItem(LOOSE_GOLD_PURITY_KEY) === '995' ? '995' : '999';
  } catch {
    return '999';
  }
}

function convertG999(grams999, purity) {
  const n = Number(grams999) || 0;
  return purity === '995' ? n / 0.995 : n;
}

// Convert a 999-basis per-gram RATE into the displayed basis so that
// grams(displayed) × rate(displayed) === value stays invariant on toggle.
// Since grams_995 = grams_999 / 0.995, the rate must move inversely:
//     rate_995 = rate_999 × 0.995
function convertRate(rate999, purity) {
  const r = Number(rate999) || 0;
  return purity === '995' ? r * 0.995 : r;
}

// Derived per-gram rate for an accumulated balance row: value ÷ grams
// (in the displayed basis). Guards divide-by-zero.
function derivedRate(value, grams999, purity) {
  const g = convertG999(grams999, purity);
  return Math.abs(g) > 1e-9 ? (Number(value) || 0) / g : 0;
}

function purityUnitLabel(purity) {
  return purity === '995' ? '995' : '999';
}

// Segmented Gold 999 / Gold 995 toggle (amber-themed to match gold accents).
function GoldPurityToggle({ purity, onChange, testId = 'loose-gold-purity-toggle' }) {
  const opts = [
    { v: '999', label: 'Gold 999', hint: '100%' },
    { v: '995', label: 'Gold 995', hint: '99.5%' },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-amber-300 overflow-hidden text-[11px] shadow-sm"
      role="group"
      aria-label="Gold purity view"
      data-testid={testId}
    >
      {opts.map((o) => {
        const active = purity === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            aria-pressed={active}
            className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
              active ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-amber-50'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.label} <span className="opacity-70">({o.hint})</span>
          </button>
        );
      })}
    </div>
  );
}

// Segmented Oldest / Newest date-sort toggle.
function DateSortToggle({ sort, onChange, testId = 'loose-gold-sort-toggle' }) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-[11px] shadow-sm" role="group" aria-label="Sort by date" data-testid={testId}>
      <button type="button" onClick={() => onChange('asc')} aria-pressed={sort === 'asc'} className={`px-3 py-1.5 font-medium transition-colors ${sort === 'asc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-asc`}>
        <ArrowUp size={12} className="inline mr-1 -mt-0.5" />Oldest
      </button>
      <button type="button" onClick={() => onChange('desc')} aria-pressed={sort === 'desc'} className={`px-3 py-1.5 font-medium transition-colors ${sort === 'desc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-desc`}>
        <ArrowDown size={12} className="inline mr-1 -mt-0.5" />Newest
      </button>
    </div>
  );
}

// Segmented Detailed / Merged ledger-view toggle.
function LedgerViewToggle({ mode, onChange, testId = 'loose-gold-view-toggle' }) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-[11px] shadow-sm" role="group" aria-label="Ledger view" data-testid={testId}>
      <button type="button" onClick={() => onChange('detailed')} aria-pressed={mode === 'detailed'} className={`px-3 py-1.5 font-medium transition-colors ${mode === 'detailed' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-detailed`}>
        <List size={12} className="inline mr-1 -mt-0.5" />Detailed
      </button>
      <button type="button" onClick={() => onChange('merged')} aria-pressed={mode === 'merged'} className={`px-3 py-1.5 font-medium transition-colors ${mode === 'merged' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-merged`}>
        <Layers size={12} className="inline mr-1 -mt-0.5" />Merged
      </button>
    </div>
  );
}

// IST calendar-day key ('YYYY-MM-DD') used to group merged-view rows.
function istDayKey(ts) {
  try { return new Date(ts).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); }
  catch { return String(ts || '').slice(0, 10); }
}


export default function Gold() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Gold (999)" subtitle="Purities, daily rates, and loose gold ledger in 999 pure equivalent.">
      <CompanyGate>
        <Tabs defaultValue="rates">
          <TabsList className="bg-white border border-[var(--divider)]">
            <TabsTrigger value="rates" data-testid="gold-tab-rates">Daily Rates</TabsTrigger>
            <TabsTrigger value="purities" data-testid="gold-tab-purities">Purities</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="gold-tab-ledger">Ledger</TabsTrigger>
          </TabsList>
          <TabsContent value="rates" className="mt-5"><RatesTab companyId={effectiveCompanyId} /></TabsContent>
          <TabsContent value="purities" className="mt-5"><PuritiesTab companyId={effectiveCompanyId} /></TabsContent>
          <TabsContent value="ledger" className="mt-5"><LedgerTab companyId={effectiveCompanyId} /></TabsContent>
        </Tabs>
      </CompanyGate>
    </AppShell>
  );
}

function PuritiesTab({ companyId }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', percent: '', description: '' });
  const [delTarget, setDelTarget] = useState(null);
  const load = async () => {
    const { data } = await api.get('/gold/purities', { params: { company_id: companyId } });
    setList(data);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);
  const create = async () => {
    try {
      await api.post('/gold/purities', { ...form, percent: Number(form.percent), company_id: companyId });
      toast.success('Purity created'); setOpen(false); setForm({ name: '', percent: '', description: '' }); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Gold Purities</div>
          <div className="text-xs text-slate-500">Define your company’s Karat → % mapping (e.g. 22Kt → 91.6%).</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="luxury-button" data-testid="purity-add-button"><Plus size={14} className="mr-1.5" /> New Purity</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Gold Purity</DialogTitle><DialogDescription>Define a karat name and its % gold content for this company.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 22Kt" data-testid="purity-name-input" /></div>
              <div><Label>Percent (%)</Label><Input type="number" step="0.01" min="0" max="100" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} placeholder="e.g. 91.60" data-testid="purity-percent-input" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} className="luxury-button" data-testid="purity-submit-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table">
          <table className="w-full">
            <thead><tr><th className="text-left">Name</th><th className="text-right">Percent</th><th className="text-left">Description</th><th className="text-right">Actions</th></tr></thead>
            <tbody data-testid="purities-table-body">
              {list.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500">No purities yet</td></tr>}
              {list.map((p) => (
                <tr key={p.id} data-testid={`purity-row-${p.id}`}>
                  <td><Badge variant="secondary">{p.name}</Badge></td>
                  <td className="text-right font-mono tabular-nums">{p.percent.toFixed(2)}%</td>
                  <td className="text-xs text-slate-500">{p.description}</td>
                  <td className="text-right"><button onClick={() => setDelTarget(p)} className="text-xs text-red-600 hover:underline" data-testid={`purity-delete-${p.id}`}><Trash2 size={12} className="inline mr-1" />Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="gold_purity"
          entity_id={delTarget.id}
          entity_label={`${delTarget.name} (${delTarget.percent}%)`}
          company_id={companyId}
          onSuccess={() => { setDelTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function RatesTab({ companyId }) {
  const { isSuperAdmin, isCompanyAdmin } = useAuth();
  const isAdmin = isSuperAdmin || isCompanyAdmin;
  const [list, setList] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState({ rate_per_gram_999: '', timestamp: nowISTDatetimeLocal(), note: '' });
  // Back-date rate lookup state.
  const [lookupDate, setLookupDate] = useState(toISODateInput(new Date()));
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const load = async () => {
    const { data } = await api.get('/gold/rates', { params: { company_id: companyId, limit: 100 } });
    setList(data);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);
  const submit = async () => {
    try {
      await api.post('/gold/rates', {
        rate_per_gram_999: Number(form.rate_per_gram_999),
        timestamp: istDatetimeLocalToISO(form.timestamp),
        note: form.note, company_id: companyId
      });
      toast.success('Rate recorded'); setForm({ ...form, rate_per_gram_999: '', note: '', timestamp: nowISTDatetimeLocal() }); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };
  const runLookup = async () => {
    if (!lookupDate) { toast.error('Pick a date first'); return; }
    setLookupLoading(true); setLookupResult(null);
    try {
      const { data } = await api.get('/gold/rates/history-lookup', {
        params: { date: lookupDate, company_id: companyId },
      });
      setLookupResult(data);
    } catch (e) {
      toast.error(extractError(e, 'Lookup failed'));
    } finally {
      setLookupLoading(false);
    }
  };
  return (
    <div className="space-y-5">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="p-5 lg:col-span-1">
        <div className="text-base font-medium text-[var(--ink-navy)]">Add Daily Rate</div>
        <div className="text-xs text-slate-500">Record 999 gold rate (₹ per gram). Use latest of the day for calculations.</div>
        <div className="space-y-3 mt-4">
          <div><Label>Rate (₹ per gram of 999)</Label><Input type="number" step="0.01" value={form.rate_per_gram_999} onChange={(e) => setForm({ ...form, rate_per_gram_999: e.target.value })} data-testid="rate-rate-input" /></div>
          <div><Label>Timestamp</Label><Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} data-testid="rate-timestamp-input" /></div>
          <div><Label>Note (optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          <Button onClick={submit} className="luxury-button w-full" data-testid="rate-submit-button">Record Rate</Button>
        </div>
      </Card>
      <Card className="p-0 lg:col-span-2 overflow-hidden">
        <div className="p-4 border-b border-[var(--divider)]">
          <div className="text-base font-medium text-[var(--ink-navy)]">Rate History</div>
          <div className="text-xs text-slate-500">Latest rates first — multiple per day allowed; latest wins.</div>
        </div>
        <div className="ledger-table">
          <table className="w-full">
            <thead><tr><th className="text-left">Timestamp</th><th className="text-right">Rate (₹/g 999)</th><th className="text-left">By</th><th className="text-left">Note</th>{isAdmin && <th className="text-right">Actions</th>}</tr></thead>
            <tbody data-testid="rates-table-body">
              {list.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-slate-500">No rates recorded yet</td></tr>}
              {list.map((r) => (
                <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                  <td className="font-mono text-xs">{formatDateTime(r.timestamp)}</td>
                  <td className="text-right font-mono tabular-nums">{formatINR(r.rate_per_gram_999)}</td>
                  <td className="text-xs text-slate-500">{r.created_by_email}</td>
                  <td className="text-xs text-slate-500">{r.note}</td>
                  {isAdmin && (
                    <td className="text-right">
                      <button
                        onClick={() => setDelTarget(r)}
                        className="text-xs text-red-600 hover:underline"
                        data-testid={`rate-delete-${r.id}`}
                        title={isSuperAdmin ? 'Delete this rate' : 'Request deletion of this rate'}
                      >
                        <Trash2 size={12} className="inline mr-1" />Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
    {delTarget && (
      <DeleteConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => { if (!v) setDelTarget(null); }}
        entity_type="gold_rate"
        entity_id={delTarget.id}
        entity_label={`${formatINR(delTarget.rate_per_gram_999)}/g · ${formatDateTime(delTarget.timestamp)}`}
        company_id={companyId}
        onSuccess={() => { setDelTarget(null); load(); }}
      />
    )}

    {/* Back-date rate lookup — pick any past date. If a rate exists on that
        exact date, show every rate of that day; otherwise show the nearest
        rate before and after (whichever exist), always with their dates. */}
    <Card className="p-5" data-testid="rate-lookup-card">
      <div className="flex items-center gap-2">
        <CalendarDays size={16} className="text-[var(--gold-icon)]" />
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Historical Rate Lookup</div>
          <div className="text-xs text-slate-500">Check the 999 gold rate on a past date. No exact match? We show the closest rate before and after.</div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mt-4">
        <div>
          <Label className="text-[11px] text-slate-600 mb-1 block">Select date</Label>
          <DatePicker
            value={lookupDate}
            maxDate={toISODateInput(new Date())}
            onChange={(e) => setLookupDate(e.target.value)}
            className="h-9 w-48"
            data-testid="rate-lookup-date"
          />
        </div>
        <Button onClick={runLookup} disabled={lookupLoading} className="luxury-button h-9" data-testid="rate-lookup-button">
          <Search size={14} className="mr-1.5" /> {lookupLoading ? 'Looking up…' : 'Look up rate'}
        </Button>
      </div>

      {lookupResult && (
        <div className="mt-5" data-testid="rate-lookup-result">
          {lookupResult.has_exact ? (
            <div>
              <div className="text-sm font-medium text-emerald-800 mb-2">
                {lookupResult.exact.length} rate{lookupResult.exact.length > 1 ? 's' : ''} recorded on{' '}
                <span className="font-mono">{lookupResult.selected_date}</span>
              </div>
              <div className="ledger-table rounded-md border border-[var(--divider)] overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr><th className="text-left">Time</th><th className="text-right">Rate (₹/g 999)</th><th className="text-left">By</th><th className="text-left">Note</th></tr></thead>
                  <tbody>
                    {lookupResult.exact.map((r) => (
                      <tr key={r.id} data-testid={`rate-lookup-exact-row-${r.id}`}>
                        <td className="font-mono text-xs">{formatDateTime(r.timestamp)}</td>
                        <td className="text-right font-mono tabular-nums text-[var(--ink-navy)]">{formatINR(r.rate_per_gram_999)}</td>
                        <td className="text-xs text-slate-500">{r.created_by_email}</td>
                        <td className="text-xs text-slate-500">{r.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (!lookupResult.before && !lookupResult.after) ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-center gap-2" data-testid="rate-lookup-empty">
              <AlertTriangle size={15} /> No gold rates recorded in the system yet.
            </div>
          ) : (
            <div>
              <div className="text-sm text-slate-600 mb-2">
                No rate was recorded on{' '}
                <span className="font-medium text-[var(--ink-navy)]">{lookupResult.selected_date}</span>. Showing the nearest rates around it:
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Nearest before */}
                <div className="rounded-md border border-[var(--divider)] p-3.5" data-testid="rate-lookup-before">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    <ArrowLeft size={12} /> Nearest before
                  </div>
                  {lookupResult.before ? (
                    <>
                      <div className="text-xl font-semibold text-[var(--ink-navy)] font-mono tabular-nums">{formatINR(lookupResult.before.rate_per_gram_999)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">as on <span className="font-medium text-slate-700">{formatDateTime(lookupResult.before.timestamp)}</span></div>
                      {lookupResult.before.note ? <div className="text-[11px] text-slate-400 mt-1">{lookupResult.before.note}</div> : null}
                    </>
                  ) : (
                    <div className="text-sm text-slate-400 italic">No earlier rate on record.</div>
                  )}
                </div>
                {/* Nearest after */}
                <div className="rounded-md border border-[var(--divider)] p-3.5" data-testid="rate-lookup-after">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    Nearest after <ArrowRight size={12} />
                  </div>
                  {lookupResult.after ? (
                    <>
                      <div className="text-xl font-semibold text-[var(--ink-navy)] font-mono tabular-nums">{formatINR(lookupResult.after.rate_per_gram_999)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">as on <span className="font-medium text-slate-700">{formatDateTime(lookupResult.after.timestamp)}</span></div>
                      {lookupResult.after.note ? <div className="text-[11px] text-slate-400 mt-1">{lookupResult.after.note}</div> : null}
                    </>
                  ) : (
                    <div className="text-sm text-slate-400 italic">No later rate on record.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
    </div>
  );
}

function LedgerTab({ companyId }) {
  // Wave 3.41 — Restricted users hide Rate / Value columns + monetary
  // summary tiles entirely. Quantity columns (999 g) remain visible.
  const { canViewValues, hasPermission } = useAuth();
  // Whether this user can see the Vendors / Goldsmiths master lists. These
  // are gated by their OWN permissions (vendors.view / goldsmiths.view); a
  // gold-scoped operator may lack them. Used to (a) show a friendly access
  // hint and (b) tailor the empty party-picker message (permission vs empty).
  const canSeeVendors = hasPermission('vendors.view');
  const canSeeGoldsmiths = hasPermission('goldsmiths.view');
  const [entries, setEntries] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [goldsmiths, setGoldsmiths] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [purities, setPurities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  // Wave 3.35 — Period filter. Defaults to "all time" (empty fields).
  // ``from`` is treated as start-of-day (00:00:00 IST) and ``to`` is
  // treated as end-of-day (23:59:59 IST) so the user picks intuitive
  // calendar dates without thinking about times.
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [form, setForm] = useState({
    type: 'receipt', weight_grams: '', purity_percent: '100',
    party_type: 'vendor', party_id: '', party_name: '',
    reference: '', note: '', timestamp: nowISTDatetimeLocal(),
    rate_per_gram_999: '', // Phase 10 — optional override; empty = use latest from master
    credit_days: '', // Wave 3.36 — optional credit days for vendor purchase entries
    advance_gold: false, // Wave 4.4 — client gold received as advance
  });
  const [rateInfo, setRateInfo] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnGoldsmithId, setReturnGoldsmithId] = useState('');
  const [returnPick, setReturnPick] = useState(false);
  // Phase 10 — Inline creation dialogs
  const [vendorDlgOpen, setVendorDlgOpen] = useState(false);
  const [purityDlgOpen, setPurityDlgOpen] = useState(false);
  // Phase 14.3 — Issue Gold (multi-goldsmith composer)
  const [issueDlgOpen, setIssueDlgOpen] = useState(false);
  // Loose Gold Ledger view controls: purity basis (999/995), date sort, PDF.
  const [purity, setPurity] = useState(readLoosePurity());
  const [sortDir, setSortDir] = useState('asc');
  // Detailed vs Merged ledger view (persisted). Default = detailed.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(LOOSE_GOLD_VIEW_MODE_KEY) === 'merged' ? 'merged' : 'detailed'; }
    catch { return 'detailed'; }
  });
  useEffect(() => { try { localStorage.setItem(LOOSE_GOLD_VIEW_MODE_KEY, viewMode); } catch { /* noop */ } }, [viewMode]);
  // Group currently expanded in the merged-view breakdown dialog.
  const [mergedDetail, setMergedDetail] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Rate / Value column toggles (only meaningful when canViewValues). Persist.
  const [showRate, setShowRate] = useState(() => readLooseColPref(LOOSE_GOLD_SHOW_RATE_KEY));
  const [showValue, setShowValue] = useState(() => readLooseColPref(LOOSE_GOLD_SHOW_VALUE_KEY));
  useEffect(() => { try { localStorage.setItem(LOOSE_GOLD_SHOW_RATE_KEY, showRate ? '1' : '0'); } catch { /* noop */ } }, [showRate]);
  useEffect(() => { try { localStorage.setItem(LOOSE_GOLD_SHOW_VALUE_KEY, showValue ? '1' : '0'); } catch { /* noop */ } }, [showValue]);
  // Effective column visibility — values are gated by permission first, then
  // by the user's toggle. Grams columns are always visible.
  const showRateCol = canViewValues && showRate;
  const showValueCol = canViewValues && showValue;
  // Group = grams (always) + Rate? + Value?. Three groups (Receipt/Issue/Balance)
  // plus the 3 fixed leading columns (Date, Particulars, {unit} Equivalent).
  const perGroupCols = 1 + (showRateCol ? 1 : 0) + (showValueCol ? 1 : 0);
  const totalCols = 3 + 3 * perGroupCols;
  const unit = purityUnitLabel(purity);
  const changePurity = (v) => {
    const next = v === '995' ? '995' : '999';
    setPurity(next);
    try { localStorage.setItem(LOOSE_GOLD_PURITY_KEY, next); } catch { /* noop */ }
  };

  const load = async () => {
    // Translate the YYYY-MM-DD UI inputs into full ISO timestamps so the
    // backend's `start`/`end` range covers the entire day inclusively.
    const params = { company_id: companyId, limit: 5000 };
    if (period.from) params.start = new Date(`${period.from}T00:00:00`).toISOString();
    if (period.to) params.end = new Date(`${period.to}T23:59:59.999`).toISOString();
    // Critical gold data — every one of these is gated ONLY by
    // ``materials.gold.see`` (the same permission required to open this page),
    // so they always succeed together and MUST render the ledger + summary.
    const [le, sm, p] = await Promise.all([
      api.get('/gold/ledger', { params }),
      api.get('/gold/ledger/summary', { params }),
      api.get('/gold/purities', { params: { company_id: companyId } }),
    ]);
    setEntries(le.data); setSummary(sm.data); setPurities(p.data);
    // Master lists are gated by their OWN permissions (``vendors.view`` /
    // ``goldsmiths.view``). A gold-scoped operator may legitimately lack these,
    // so a 403 here must NOT blank out the ledger (previously they shared the
    // critical Promise.all, so one 403 nuked the whole ledger + tiles). Fetch
    // them independently and swallow errors — the New Entry / Issue Gold party
    // pickers simply show no options for such users. Mirrors the defensive
    // customer fetch below.
    api.get('/masters/vendors', { params: { company_id: companyId } })
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVendors([]));
    api.get('/masters/goldsmiths', { params: { company_id: companyId } })
      .then((r) => setGoldsmiths(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGoldsmiths([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (companyId) load(); }, [companyId, period.from, period.to]);

  // Customers are fetched separately (and defensively) because the customer
  // master read is gated by ``orders.view`` — a gold-only operator may lack it.
  // A 403 here must NOT break the Gold page, so we swallow errors and simply
  // leave the "Client" source with no options for such users.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    api.get('/masters/customers', { params: { company_id: companyId } })
      .then((r) => { if (!cancelled) setCustomers(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setCustomers([]); });
    return () => { cancelled = true; };
  }, [companyId]);

  // Lookup rate when timestamp changes
  useEffect(() => {
    let cancelled = false;
    async function lookup() {
      try {
        const { data } = await api.get('/gold/rates/lookup', { params: { date: istDatetimeLocalToISO(form.timestamp), company_id: companyId } });
        if (!cancelled) setRateInfo(data);
      } catch {}
    }
    if (companyId && form.timestamp) lookup();
    return () => { cancelled = true; };
  }, [form.timestamp, companyId]);

  const submit = async () => {
    try {
      const isCustomer = form.type === 'receipt' && form.party_type === 'customer';
      const customRate = Number(form.rate_per_gram_999);
      // Client gold: the rate is a mandatory, manually-entered reference figure.
      if (isCustomer && !(form.rate_per_gram_999 && customRate > 0)) {
        toast.error('Enter the client-agreed gold rate (reference).');
        return;
      }
      if (isCustomer && !form.party_id) {
        toast.error('Select the client the gold was received from.');
        return;
      }
      const resolvedName = form.party_name || (
        form.type === 'issue'
          ? goldsmiths.find((g) => g.id === form.party_id)?.name
          : (form.party_type === 'customer'
            ? customers.find((c) => c.id === form.party_id)?.name
            : vendors.find((v) => v.id === form.party_id)?.name)
      );
      const payload = {
        type: form.type, weight_grams: Number(form.weight_grams),
        purity_percent: Number(form.purity_percent),
        party_type: form.party_type, party_id: form.party_id || null,
        party_name: resolvedName,
        reference: form.reference, note: form.note,
        timestamp: istDatetimeLocalToISO(form.timestamp),
        company_id: companyId,
      };
      // Phase 10 — Optional rate override; empty = use latest system rate
      // (fallback handled by backend). For client gold it is required (above).
      if (form.rate_per_gram_999 && customRate > 0) payload.rate_per_gram_999 = customRate;
      // Wave 4.4 — Advance Gold flag only for client (customer) receipts.
      if (isCustomer) payload.advance_gold = !!form.advance_gold;
      // Wave 3.36 — Credit days only meaningful for vendor receipts (creates payable).
      if (form.type === 'receipt' && form.party_type === 'vendor' && form.credit_days !== '') {
        const n = parseInt(form.credit_days, 10);
        if (!Number.isNaN(n) && n > 0) payload.credit_days = n;
      }
      await api.post('/gold/ledger', payload);
      toast.success(isCustomer ? 'Client gold recorded — added to stock + customer ledger' : 'Entry recorded');
      setOpen(false);
      setForm({ ...form, weight_grams: '', reference: '', note: '', rate_per_gram_999: '', credit_days: '', advance_gold: false });
      load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  const partyList = form.type === 'issue'
    ? goldsmiths
    : (form.party_type === 'customer' ? customers : vendors);
  const partyLabel = form.type === 'issue'
    ? 'Goldsmith'
    : (form.party_type === 'customer' ? 'Client' : 'Vendor');
  const isCustomerReceipt = form.type === 'receipt' && form.party_type === 'customer';

  // Display-order entries (backend returns ascending with running balances
  // already attached; newest-first is a pure display reversal).
  const displayEntries = sortDir === 'desc' ? [...entries].reverse() : entries;

  // Merged view — group same-day entries for the same party, split by type
  // (Issues → goldsmith; Receipts → vendor / customer / goldsmith return).
  // Totals sum the movement grams/value; the Balance shown is the running
  // balance after the group's LATEST entry (its closing balance). Groups are
  // ordered by that latest timestamp and honour the Oldest/Newest toggle.
  const mergedGroups = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const day = istDayKey(e.timestamp);
      const pkey = e.party_id || ('n:' + String(e.party_name || '').trim().toLowerCase());
      const key = `${e.type}|${pkey}|${day}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key, type: e.type, party_name: e.party_name || '—', party_id: e.party_id || null,
          party_type: e.party_type || '', sub_type: e.sub_type || '', day,
          entries: [], total_pure_grams_999: 0, total_value: 0, rep: e,
        };
        map.set(key, g);
      }
      g.entries.push(e);
      g.total_pure_grams_999 += Number(e.pure_grams_999 || 0);
      g.total_value += Number(e.value || 0);
      // rep = latest entry of the group (entries arrive ascending).
      if (new Date(e.timestamp).getTime() >= new Date(g.rep.timestamp).getTime()) {
        g.rep = e;
        g.sub_type = e.sub_type || g.sub_type;
      }
    }
    const groups = Array.from(map.values());
    groups.sort((a, b) => new Date(a.rep.timestamp).getTime() - new Date(b.rep.timestamp).getTime());
    if (sortDir === 'desc') groups.reverse();
    return groups;
  }, [entries, sortDir]);
  // ISO bounds for the PDF export (mirror the load() period translation).
  const pdfStart = period.from ? new Date(`${period.from}T00:00:00`).toISOString() : '';
  const pdfEnd = period.to ? new Date(`${period.to}T23:59:59.999`).toISOString() : '';

  // Opening Balance row — shown only when a From date is set. Uses the summary
  // opening figures (carried into the From date). Rate is derived value/grams.
  const openG999 = Number(summary?.opening?.pure_grams_999 || 0);
  const openVal = Number(summary?.opening?.value || 0);
  const dash = <span className="text-slate-300">—</span>;
  const openingRowEl = (
    <tr className="bg-amber-50/70 border-y border-amber-200" data-testid="loose-gold-opening-row">
      <td className="text-xs whitespace-nowrap px-2 py-2 align-top font-mono text-slate-600">{period.from}</td>
      <td className="text-xs px-2 py-2 align-top">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">Opening Balance</span>
        <span className="text-[11px] text-slate-500 ml-2">(as on {period.from})</span>
      </td>
      <td className="text-right px-2 py-2 align-top">{dash}</td>
      {/* Receipt group */}
      <td className="text-right px-2 py-2 align-top border-l border-[var(--divider)] bg-emerald-50/20">{dash}</td>
      {showRateCol && <td className="text-right px-2 py-2 align-top bg-emerald-50/20">{dash}</td>}
      {showValueCol && <td className="text-right px-2 py-2 align-top bg-emerald-50/20">{dash}</td>}
      {/* Issue group */}
      <td className="text-right px-2 py-2 align-top border-l border-[var(--divider)] bg-rose-50/20">{dash}</td>
      {showRateCol && <td className="text-right px-2 py-2 align-top bg-rose-50/20">{dash}</td>}
      {showValueCol && <td className="text-right px-2 py-2 align-top bg-rose-50/20">{dash}</td>}
      {/* Balance group */}
      <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-amber-50/40 font-semibold" data-testid="loose-gold-opening-grams">{formatGrams(convertG999(openG999, purity))}</td>
      {showRateCol && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/40">{formatINR(derivedRate(openVal, openG999, purity))}</td>}
      {showValueCol && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/40 font-semibold">{formatINR(openVal)}</td>}
    </tr>
  );

  const dashCell = <span className="text-slate-300">—</span>;

  // Format an IST day key ('YYYY-MM-DD') for the merged-row Date cell.
  const formatDayLabel = (dayKey) => {
    try {
      return new Date(`${dayKey}T12:00:00Z`).toLocaleDateString('en-GB', {
        timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return dayKey; }
  };

  // Single ledger entry row (also reused for 1-entry merged groups).
  const renderEntryRow = (e) => {
    const isReceipt = e.type === 'receipt';
    const subLabel = e.sub_type === 'goldsmith_return' ? 'Return' : (isReceipt ? 'Receipt' : 'Issue');
    const wtG = formatGrams(e.weight_grams || 0);
    const purityPct = (e.purity_percent || 0).toFixed(2);
    const purityName = e.purity_name || '';
    const particulars = (
      <>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isReceipt
            ? <Badge className="bg-emerald-50 text-emerald-700 px-1.5 py-0"><ArrowDownToLine size={10} className="mr-0.5" />{subLabel}</Badge>
            : <Badge className="bg-rose-50 text-rose-700 px-1.5 py-0"><ArrowUpFromLine size={10} className="mr-0.5" />Issue</Badge>}
          <span className="text-[var(--ink-navy)] font-medium">{e.party_name || '—'}</span>
          {e.order_id && e.order_no && (
            <Link to={`/orders/${e.order_id}`} className="text-[var(--ink-navy)] hover:underline">· {e.order_no}</Link>
          )}
          <span className="text-slate-400">·</span>
          <span
            className="text-slate-700 inline-flex items-center gap-0.5 font-mono"
            title={`Original weight: ${wtG} g of ${purityName || `${purityPct}%`} (converted to 999-equivalent in the next column).`}
          >
            {wtG}g {purityName ? <em className="not-italic text-slate-500">{purityName}</em> : null} ({purityPct}%)
          </span>
        </div>
        {e.reference ? <span className="text-slate-500">{e.reference}</span> : null}
        {(e.issue_memo_no || e.return_memo_no) && (
          <div className="mt-0.5 inline-flex gap-1 flex-wrap items-center">
            {e.issue_memo_no && <MemoLink id={e.goldsmith_issue_id} memoNo={e.issue_memo_no} testId={`gold-ledger-memo-${e.id}`} />}
            {e.return_memo_no && <ReturnMemoLink id={e.return_id} memoNo={e.return_memo_no} testId={`gold-ledger-return-memo-${e.id}`} />}
          </div>
        )}
      </>
    );
    return (
      <tr key={e.id} data-testid={`gold-ledger-row-${e.id}`} className="border-t border-[var(--divider)]">
        <td className="text-xs whitespace-nowrap px-2 py-2 align-top">{formatDateTime(e.timestamp)}</td>
        <td className="text-xs px-2 py-2 align-top max-w-md">{particulars}</td>
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top">{formatGrams(convertG999(e.pure_grams_999, purity))}</td>
        {/* Receipt group */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-emerald-50/20">
          {isReceipt ? formatGrams(convertG999(e.pure_grams_999, purity)) : dashCell}
        </td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-emerald-50/20">
          {isReceipt ? formatINR(convertRate(e.rate_per_gram_999, purity)) : dashCell}
        </td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-emerald-50/20">
          {isReceipt ? formatINR(e.value) : dashCell}
        </td>}
        {/* Issue group */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-rose-50/20">
          {!isReceipt ? formatGrams(convertG999(e.pure_grams_999, purity)) : dashCell}
        </td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-rose-50/20">
          {!isReceipt ? formatINR(convertRate(e.rate_per_gram_999, purity)) : dashCell}
        </td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-rose-50/20">
          {!isReceipt ? formatINR(e.value) : dashCell}
        </td>}
        {/* Balance group */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-amber-50/20">{formatGrams(convertG999(e.running_pure_grams_999, purity))}</td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/20">{formatINR(derivedRate(e.running_value, e.running_pure_grams_999, purity))}</td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/20">{formatINR(e.running_value)}</td>}
      </tr>
    );
  };

  // Merged (grouped) row — one per party+day. Clicking opens a breakdown
  // dialog listing every underlying entry. 1-entry groups fall back to the
  // normal detailed row so the ledger reads naturally.
  const renderMergedRow = (g) => {
    if (g.entries.length === 1) return renderEntryRow(g.entries[0]);
    const isReceipt = g.type === 'receipt';
    const subLabel = g.sub_type === 'goldsmith_return' ? 'Return' : (isReceipt ? 'Receipt' : 'Issue');
    const totalG = formatGrams(convertG999(g.total_pure_grams_999, purity));
    const grpRate = derivedRate(g.total_value, g.total_pure_grams_999, purity);
    const rep = g.rep;
    return (
      <tr
        key={g.key}
        data-testid={`gold-ledger-merged-row-${g.key}`}
        className="border-t border-[var(--divider)] cursor-pointer hover:bg-amber-50/50 transition-colors"
        onClick={() => setMergedDetail(g)}
        role="button"
        tabIndex={0}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setMergedDetail(g); } }}
        title="Click to view the individual entries merged into this row"
      >
        <td className="text-xs whitespace-nowrap px-2 py-2 align-top font-medium text-[var(--ink-navy)]">{formatDayLabel(g.day)}</td>
        <td className="text-xs px-2 py-2 align-top max-w-md">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isReceipt
              ? <Badge className="bg-emerald-50 text-emerald-700 px-1.5 py-0"><ArrowDownToLine size={10} className="mr-0.5" />{subLabel}</Badge>
              : <Badge className="bg-rose-50 text-rose-700 px-1.5 py-0"><ArrowUpFromLine size={10} className="mr-0.5" />Issue</Badge>}
            <span className="text-[var(--ink-navy)] font-medium">{g.party_name || '—'}</span>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0 gap-0.5">
              <Layers size={9} /> {g.entries.length} entries
            </Badge>
            <span className="inline-flex items-center text-slate-400"><ChevronRight size={13} /></span>
          </div>
          <span className="text-[11px] text-slate-500">Merged — click to view individual entries</span>
        </td>
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top font-semibold">{totalG}</td>
        {/* Receipt group */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-emerald-50/20 font-semibold">
          {isReceipt ? totalG : dashCell}
        </td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-emerald-50/20">
          {isReceipt ? formatINR(grpRate) : dashCell}
        </td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-emerald-50/20 font-semibold">
          {isReceipt ? formatINR(g.total_value) : dashCell}
        </td>}
        {/* Issue group */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-rose-50/20 font-semibold">
          {!isReceipt ? totalG : dashCell}
        </td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-rose-50/20">
          {!isReceipt ? formatINR(grpRate) : dashCell}
        </td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-rose-50/20 font-semibold">
          {!isReceipt ? formatINR(g.total_value) : dashCell}
        </td>}
        {/* Balance group — the running balance after the group's latest entry */}
        <td className="text-right font-mono tabular-nums px-2 py-2 align-top border-l border-[var(--divider)] bg-amber-50/20">{formatGrams(convertG999(rep.running_pure_grams_999, purity))}</td>
        {canViewValues && showRate && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/20">{formatINR(derivedRate(rep.running_value, rep.running_pure_grams_999, purity))}</td>}
        {canViewValues && showValue && <td className="text-right font-mono tabular-nums px-2 py-2 align-top bg-amber-50/20">{formatINR(rep.running_value)}</td>}
      </tr>
    );
  };

  return (
    <div>
      {/* Wave 3.35 — Period filter for the ledger summary + entries */}
      <Card className="p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="gold-ledger-period-card">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 shrink-0">Period</div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            From
            <DatePicker
              value={period.from}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
              className="h-8 text-xs w-[170px]"
              data-testid="gold-ledger-period-from"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            To
            <DatePicker
              value={period.to}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
              className="h-8 text-xs w-[170px]"
              data-testid="gold-ledger-period-to"
            />
          </label>
          {(period.from || period.to) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setPeriod({ from: '', to: '' })}
              className="h-8 px-2 text-xs"
              data-testid="gold-ledger-period-clear"
            >
              Clear
            </Button>
          )}
          <span className="text-[11px] text-slate-500 ml-1">
            {period.from || period.to
              ? `Showing ${period.from || '—'} to ${period.to || '—'}`
              : 'Showing all-time entries'}
          </span>
        </div>
      </Card>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Card className="p-4" data-testid="gold-ledger-summary-opening">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Opening{period.from ? ` (as on ${period.from})` : ''}</div>
            <div className="font-mono tabular-nums text-base text-[var(--ink-navy)] mt-1">{formatGrams(convertG999(summary.opening.pure_grams_999, purity))}g <span className="text-[10px] text-slate-400">{unit}</span></div>
            {showValueCol && <div className="text-xs text-slate-500">{formatINR(summary.opening.value)}</div>}
          </Card>
          <Card className="p-4" data-testid="gold-ledger-summary-receipts">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Period Receipts</div>
            <div className="font-mono tabular-nums text-base text-emerald-700 mt-1">+{formatGrams(convertG999(summary.period_receipts.pure_grams_999, purity))}g <span className="text-[10px] text-slate-400">{unit}</span></div>
            {showValueCol && <div className="text-xs text-slate-500">{formatINR(summary.period_receipts.value)}</div>}
          </Card>
          <Card className="p-4" data-testid="gold-ledger-summary-issues">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Period Issues</div>
            <div className="font-mono tabular-nums text-base text-rose-700 mt-1">-{formatGrams(convertG999(summary.period_issues.pure_grams_999, purity))}g <span className="text-[10px] text-slate-400">{unit}</span></div>
            {showValueCol && <div className="text-xs text-slate-500">{formatINR(summary.period_issues.value)}</div>}
          </Card>
          <Card className="p-4 bg-[var(--gold-bg)] border-[var(--gold-border)]" data-testid="gold-ledger-summary-closing">
            <div className="text-[11px] uppercase tracking-wider text-[var(--gold-icon)]">Closing{period.to ? ` (as on ${period.to})` : ''}</div>
            <div className="font-mono tabular-nums text-base text-[var(--ink-navy)] mt-1">{formatGrams(convertG999(summary.closing.pure_grams_999, purity))}g <span className="text-[10px] text-slate-400">{unit}</span></div>
            {showValueCol && <div className="text-xs text-slate-700">{formatINR(summary.closing.value)}</div>}
          </Card>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Loose Gold Ledger</div>
          <div className="text-xs text-slate-500">All entries stored as 999 pure-equivalent grams using applicable rate.</div>
          {/* Access hint — clarifies that a hidden vendor/goldsmith list is a
              permission limit, NOT a load error. The ledger itself is complete. */}
          {(!canSeeVendors || !canSeeGoldsmiths) && (
            <div
              className="mt-1.5 inline-flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500"
              data-testid="gold-master-access-hint"
            >
              <Info size={12} className="mt-0.5 shrink-0 text-slate-400" />
              <span>
                {`The ${[!canSeeVendors && 'vendor', !canSeeGoldsmiths && 'goldsmith'].filter(Boolean).join(' & ')} list ${(!canSeeVendors && !canSeeGoldsmiths) ? 'are' : 'is'} hidden by your permissions`} — your ledger is complete; this only limits the party pickers in New Entry / Issue Gold.
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            onClick={() => setIssueDlgOpen(true)}
            data-testid="gold-issue-button"
          >
            <ArrowUpFromLine size={14} className="mr-1.5" /> Issue Gold
          </Button>
          <Button
            variant="outline"
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
            onClick={() => setReturnPick(true)}
            data-testid="gold-return-button"
          >
            <ArrowDownToLine size={14} className="mr-1.5" /> Return from Goldsmith
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="luxury-button" data-testid="gold-ledger-add-button"><Plus size={14} className="mr-1.5" /> New Entry</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Gold Entry</DialogTitle><DialogDescription>Receipts add to inventory; issues deduct from it.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, party_type: v === 'receipt' ? 'vendor' : 'goldsmith', party_id: '', advance_gold: false })}>
                    <SelectTrigger data-testid="gold-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receipt">Receipt (↑ add)</SelectItem>
                      <SelectItem value="issue">Issue (↓ deduct)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Timestamp</Label><Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} data-testid="gold-timestamp-input" /></div>
              </div>
              {form.type === 'receipt' && (
                <div>
                  <Label>Source</Label>
                  <Select
                    value={form.party_type}
                    onValueChange={(v) => setForm({ ...form, party_type: v, party_id: '', party_name: '', credit_days: '', advance_gold: false })}
                  >
                    <SelectTrigger data-testid="gold-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendor">Vendor — market purchase (creates payable)</SelectItem>
                      <SelectItem value="customer">Client — gold received from a customer</SelectItem>
                    </SelectContent>
                  </Select>
                  {isCustomerReceipt && (
                    <div className="text-[11px] text-amber-700 mt-1">
                      Client gold adds to your stock and is tracked in that customer's Gold Ledger. The rate below is stored as a reference only (no payable/receivable is posted).
                    </div>
                  )}
                  {isCustomerReceipt && (
                    <label
                      className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 cursor-pointer select-none"
                      data-testid="gold-advance-checkbox-row"
                    >
                      <Checkbox
                        checked={!!form.advance_gold}
                        onCheckedChange={(v) => setForm({ ...form, advance_gold: !!v })}
                        className="mt-0.5"
                        data-testid="gold-advance-checkbox"
                      />
                      <span className="text-xs text-[var(--ink-navy)]">
                        <span className="font-semibold">Advance Gold</span>
                        <span className="block text-[11px] text-slate-500">
                          Record this client gold as advance received. It will appear in the Advance Gold report for the period.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Weight (g, 3dp)</Label><Input type="number" step="0.001" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} data-testid="gold-weight-input" /></div>
                <div>
                  <Label>Purity %</Label>
                  <div className="flex gap-2">
                    <Select value={String(form.purity_percent)} onValueChange={(v) => setForm({ ...form, purity_percent: v })}>
                      <SelectTrigger data-testid="gold-purity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">999 (100% pure)</SelectItem>
                        {purities.map((p) => <SelectItem key={p.id} value={String(p.percent)}>{p.name} ({p.percent}%)</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => setPurityDlgOpen(true)} data-testid="gold-purity-create-button" title="Create purity"><Plus size={14} /></Button>
                  </div>
                </div>
              </div>
              <div>
                <Label>{partyLabel}</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      items={partyList}
                      value={form.party_id}
                      onValueChange={(v) => setForm({ ...form, party_id: v || '' })}
                      itemToLabel={(x) => x.name}
                      itemToSubLabel={(x) => [x.phone, x.email].filter(Boolean).join(' · ') || null}
                      searchKeys={['name', 'phone', 'email']}
                      placeholder={partyList.length === 0
                        ? (((form.type === 'issue' ? canSeeGoldsmiths : (form.party_type === 'customer' ? true : canSeeVendors)))
                            ? 'No options'
                            : 'No access')
                        : 'Select…'}
                      searchPlaceholder={`Search ${partyLabel.toLowerCase()}…`}
                      disabled={partyList.length === 0}
                      testId="gold-party-select"
                    />
                  </div>
                  {form.type === 'receipt' && form.party_type === 'vendor' && (
                    <Button type="button" variant="outline" size="icon" onClick={() => setVendorDlgOpen(true)} data-testid="gold-vendor-create-button" title="Create vendor"><Plus size={14} /></Button>
                  )}
                </div>
                {isCustomerReceipt && partyList.length === 0 && (
                  <div className="text-[11px] text-slate-500 mt-1">No customers available. Add customers from the Customers page first.</div>
                )}
                {/* Empty party-picker message — differentiates a genuine "no
                    data yet" from a "hidden by permission" case so the operator
                    knows whether to add a record or ask an admin for access. */}
                {!isCustomerReceipt && partyList.length === 0 && (() => {
                  const isGs = form.type === 'issue';
                  const canSee = isGs ? canSeeGoldsmiths : canSeeVendors;
                  return (
                    <div className="text-[11px] text-slate-500 mt-1 flex items-start gap-1" data-testid="gold-party-empty-hint">
                      <Info size={12} className="mt-0.5 shrink-0 text-slate-400" />
                      {canSee ? (
                        isGs
                          ? <span>No goldsmiths yet — add one from the Goldsmiths page first.</span>
                          : <span>No vendors yet — create one with the + button.</span>
                      ) : (
                        <span>
                          You don&apos;t have access to the {isGs ? 'goldsmith' : 'vendor'} list. Ask your admin for
                          {' '}&ldquo;{isGs ? 'Goldsmiths' : 'Vendors'} — View&rdquo; access.
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <Label>
                  {isCustomerReceipt
                    ? <>Rate (₹/g 999) — client-agreed <span className="text-rose-600">*</span> <span className="text-slate-400">(reference)</span></>
                    : <>Rate (₹/g 999) <span className="text-slate-400">(optional)</span></>}
                </Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.rate_per_gram_999}
                  onChange={(e) => setForm({ ...form, rate_per_gram_999: e.target.value })}
                  placeholder={isCustomerReceipt
                    ? 'Enter the rate agreed with the client'
                    : (rateInfo?.available ? `Default: ₹${Number(rateInfo.rate_per_gram_999).toLocaleString('en-IN')}/g (from system rate)` : 'Leave empty to use latest system rate')}
                  data-testid="gold-rate-input"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  {isCustomerReceipt
                    ? 'Stored as a reference figure only — it does not post to any amount ledger.'
                    : 'If left empty, the latest gold rate from the Rates master will be used.'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              </div>
              {/* Wave 3.36 — Credit days input (vendor receipts only) */}
              {form.type === 'receipt' && form.party_type === 'vendor' && (() => {
                const v = vendors.find((vv) => vv.id === form.party_id);
                const defaultDays = v?.default_credit_days != null && v.default_credit_days > 0 ? v.default_credit_days : null;
                // Auto-prefill from vendor default when user hasn't typed
                if (defaultDays != null && form.credit_days === '' && form.party_id) {
                  setTimeout(() => setForm((f) => f.credit_days === '' ? { ...f, credit_days: String(defaultDays) } : f), 0);
                }
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Credit Days <span className="text-slate-400 text-[10px]">(optional)</span></Label>
                      <Input
                        type="number" min={0} max={365}
                        placeholder={defaultDays ? `Default ${defaultDays}` : 'e.g. 30 — leave blank for none'}
                        value={form.credit_days}
                        onChange={(e) => setForm({ ...form, credit_days: e.target.value.replace(/[^0-9]/g, '') })}
                        data-testid="gold-credit-days-input"
                      />
                    </div>
                    <div className="self-end text-[11px] text-slate-500">
                      {form.credit_days && Number(form.credit_days) > 0 ? (
                        <span>Due date will be ≈ <strong>{(() => {
                          const baseIso = form.timestamp ? istDatetimeLocalToISO(form.timestamp) : new Date().toISOString();
                          const due = new Date(new Date(baseIso).getTime() + Number(form.credit_days) * 86400000);
                          return due.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
                        })()}</strong>.</span>
                      ) : (
                        <span className="text-slate-400">No due date / no reminders.</span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {rateInfo && rateInfo.available && (
                <Card className="p-3 bg-[var(--paper-warm)]">
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    {!rateInfo.is_exact_date && <AlertTriangle size={12} className="text-amber-600" />}
                    Applicable rate {rateInfo.is_exact_date ? '' : '(fallback to last available)'}:
                  </div>
                  <div className="text-sm font-mono text-[var(--ink-navy)]">
                    {formatINR(rateInfo.rate_per_gram_999)}/g · {formatDateTime(rateInfo.timestamp)}
                  </div>
                  {Number(form.weight_grams) > 0 && (
                    <div className="text-xs text-slate-500 mt-1">
                      Value: {formatINR(Number(form.weight_grams) * (Number(form.purity_percent)/100) * rateInfo.rate_per_gram_999)}
                    </div>
                  )}
                </Card>
              )}
              {rateInfo && !rateInfo.available && (
                <Card className="p-3 bg-rose-50 border-rose-200">
                  <div className="text-xs text-rose-700 flex items-center gap-1"><AlertTriangle size={12} /> No gold rate available for this date. Please add a rate in the Rates tab first.</div>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="luxury-button" disabled={!rateInfo?.available} data-testid="gold-ledger-submit-button">Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {/* Ledger view controls: date sort · purity basis · column toggles · PDF export */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="loose-gold-ledger-controls">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">View</span>
        <DateSortToggle sort={sortDir} onChange={setSortDir} testId="loose-gold-sort-toggle" />
        <LedgerViewToggle mode={viewMode} onChange={setViewMode} testId="loose-gold-view-toggle" />
        <GoldPurityToggle purity={purity} onChange={changePurity} testId="loose-gold-purity-toggle" />
        {canViewValues && (
          <div className="inline-flex items-center gap-3 pl-2 ml-1 border-l border-[var(--divider)]" data-testid="loose-gold-column-toggles">
            <span className="text-[11px] text-slate-600 font-medium">Columns</span>
            <label className="flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer select-none">
              <Checkbox checked={showRate} onCheckedChange={(v) => setShowRate(!!v)} data-testid="loose-gold-toggle-rate" />
              Rate
            </label>
            <label className="flex items-center gap-1.5 text-[12.5px] text-slate-700 cursor-pointer select-none">
              <Checkbox checked={showValue} onCheckedChange={(v) => setShowValue(!!v)} data-testid="loose-gold-toggle-value" />
              Value
            </label>
          </div>
        )}
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setPdfOpen(true)}
            disabled={entries.length === 0}
            data-testid="loose-gold-print-button"
          >
            <Printer size={14} className="mr-1.5" /> Download PDF
          </Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          {/* Wave 3.24 — Restructured the Loose Gold Ledger as a classic
              Receipts / Issues / Balance ledger with grouped column
              headers. Each group has its own (999 g · Rate · Value)
              sub-columns. Purity is shown inline in Particulars instead
              of as a separate column. */}
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase">
              <tr>
                <th rowSpan={2} className="text-left px-2 py-2 align-middle font-medium">Date</th>
                <th rowSpan={2} className="text-left px-2 py-2 align-middle font-medium">Particulars</th>
                <th rowSpan={2} className="text-right px-2 py-2 align-middle font-medium" title={`The ${unit}-pure equivalent grams of this movement, derived from weight × purity %.`}>{unit} Equivalent</th>
                {/* Sub-columns Rate / Value hidden when !canViewValues or toggled off */}
                <th colSpan={perGroupCols} className="text-center px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-emerald-50/60 text-emerald-700">Receipt</th>
                <th colSpan={perGroupCols} className="text-center px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-rose-50/60 text-rose-700">Issue</th>
                <th colSpan={perGroupCols} className="text-center px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-amber-50/60 text-amber-800">Balance</th>
              </tr>
              <tr className="text-[10px] tracking-wide">
                <th className="text-right px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-emerald-50/40">{unit} g</th>
                {showRateCol && <th className="text-right px-2 py-1.5 font-medium bg-emerald-50/40">Rate</th>}
                {showValueCol && <th className="text-right px-2 py-1.5 font-medium bg-emerald-50/40">Value</th>}
                <th className="text-right px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-rose-50/40">{unit} g</th>
                {showRateCol && <th className="text-right px-2 py-1.5 font-medium bg-rose-50/40">Rate</th>}
                {showValueCol && <th className="text-right px-2 py-1.5 font-medium bg-rose-50/40">Value</th>}
                <th className="text-right px-2 py-1.5 font-medium border-l border-[var(--divider)] bg-amber-50/40">{unit} g</th>
                {showRateCol && <th className="text-right px-2 py-1.5 font-medium bg-amber-50/40">Rate</th>}
                {showValueCol && <th className="text-right px-2 py-1.5 font-medium bg-amber-50/40">Value</th>}
              </tr>
            </thead>
            <tbody data-testid="gold-ledger-table-body">
              {entries.length === 0 && !period.from && <tr><td colSpan={totalCols} className="text-center py-10 text-slate-500">No entries yet.</td></tr>}
              {period.from && sortDir !== 'desc' && openingRowEl}
              {viewMode === 'merged'
                ? mergedGroups.map(renderMergedRow)
                : displayEntries.map(renderEntryRow)}
              {period.from && sortDir === 'desc' && openingRowEl}
              {entries.length === 0 && period.from && (
                <tr><td colSpan={totalCols} className="text-center py-6 text-slate-400 text-xs">No movements in the selected period — only the opening balance applies.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {returnPick && (
        <Dialog open={returnPick} onOpenChange={setReturnPick}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Pick goldsmith to return from</DialogTitle>
              <DialogDescription>Select which goldsmith is returning gold. The next step lets you choose specific buckets and quantities.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {(goldsmiths || []).length === 0 && (
                <div className="text-sm text-slate-500 py-4 text-center border border-dashed rounded">No goldsmiths defined.</div>
              )}
              {(goldsmiths || []).map((g) => (
                <button
                  key={g.id}
                  onClick={() => { setReturnGoldsmithId(g.id); setReturnPick(false); setReturnOpen(true); }}
                  className="w-full flex items-center justify-between gap-2 p-3 border border-[var(--divider)] rounded-md hover:bg-slate-50 text-left"
                  data-testid={`gold-return-pick-${g.id}`}
                >
                  <span className="text-sm font-medium text-[var(--ink-navy)]">{g.name}</span>
                  <span className="text-xs text-slate-500">{g.phone || ''}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {returnOpen && returnGoldsmithId && (
        <ReturnDialog
          open={returnOpen}
          onOpenChange={(v) => { setReturnOpen(v); if (!v) setReturnGoldsmithId(''); }}
          companyId={companyId}
          goldsmithId={returnGoldsmithId}
          goldsmithName={(goldsmiths.find((g) => g.id === returnGoldsmithId) || {}).name}
          filter={{ material: 'gold' }}
          onSuccess={() => { setReturnOpen(false); setReturnGoldsmithId(''); load(); }}
        />
      )}

      {/* Phase 10 — inline purity + vendor creation dialogs */}
      <PurityFormDialog
        open={purityDlgOpen}
        onOpenChange={setPurityDlgOpen}
        companyId={companyId}
        onCreated={async (p) => { await load(); setForm((f) => ({ ...f, purity_percent: String(p.percent) })); }}
      />
      <VendorFormDialog
        open={vendorDlgOpen}
        onOpenChange={setVendorDlgOpen}
        companyId={companyId}
        onCreated={async (v) => { await load(); setForm((f) => ({ ...f, party_id: v.id, party_name: v.name })); }}
      />

      {/* Phase 14.3 — Issue Gold (multi-goldsmith composer) */}
      <GoldIssueDialog
        open={issueDlgOpen}
        onOpenChange={setIssueDlgOpen}
        companyId={companyId}
        goldsmiths={goldsmiths}
        purities={purities}
        availableGrams999={Number(summary?.closing?.pure_grams_999 || 0)}
        onIssued={() => { load(); }}
        onGoldsmithCreated={() => { load(); }}
      />

      {/* Loose Gold Ledger — PDF export (mirrors period + sort + purity view + column toggles) */}
      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title="Download Loose Gold Ledger PDF"
        description="A4 landscape PDF mirroring the on-screen ledger (period, sort, Gold 999/995 view and Rate/Value columns)."
        companyId={companyId}
        endpoint="/gold/ledger/pdf"
        extraParams={{ company_id: companyId, start: pdfStart, end: pdfEnd, purity, sort: sortDir, show_rate: showRate, show_value: showValue }}
        filename="Loose_Gold_Ledger.pdf"
        mode="download"
      />

      {/* Merged-view breakdown — lists every entry merged into a grouped row. */}
      <Dialog open={!!mergedDetail} onOpenChange={(v) => { if (!v) setMergedDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="gold-ledger-merged-detail-dialog">
          {mergedDetail && (() => {
            const g = mergedDetail;
            const isReceipt = g.type === 'receipt';
            const subLabel = g.sub_type === 'goldsmith_return' ? 'Return' : (isReceipt ? 'Receipt' : 'Issue');
            const totalG = formatGrams(convertG999(g.total_pure_grams_999, purity));
            const ordered = [...g.entries].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Layers size={16} className="text-amber-600" />
                    {subLabel} · {g.party_name}
                  </DialogTitle>
                  <DialogDescription>
                    {g.entries.length} entries on {formatDayLabel(g.day)} · merged into one row.
                    Totals are the sum of these movements.
                  </DialogDescription>
                </DialogHeader>
                <div className="rounded-md border border-[var(--divider)] overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 uppercase text-[10px]">
                      <tr>
                        <th className="text-left px-2 py-2 font-medium">Time</th>
                        <th className="text-left px-2 py-2 font-medium">Order / Reference</th>
                        <th className="text-right px-2 py-2 font-medium">{unit} g</th>
                        {showRateCol && <th className="text-right px-2 py-2 font-medium">Rate</th>}
                        {showValueCol && <th className="text-right px-2 py-2 font-medium">Value</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {ordered.map((e) => {
                        const purityName = e.purity_name || '';
                        const purityPct = (e.purity_percent || 0).toFixed(2);
                        return (
                          <tr key={e.id} className="border-t border-[var(--divider)]" data-testid={`gold-ledger-merged-detail-row-${e.id}`}>
                            <td className="px-2 py-2 align-top whitespace-nowrap text-slate-600">{formatDateTime(e.timestamp)}</td>
                            <td className="px-2 py-2 align-top">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {e.order_id && e.order_no
                                  ? <Link to={`/orders/${e.order_id}`} className="text-[var(--ink-navy)] font-medium hover:underline">{e.order_no}</Link>
                                  : <span className="text-slate-400">—</span>}
                                <span className="text-slate-500 font-mono text-[11px]">
                                  {formatGrams(e.weight_grams || 0)}g {purityName ? <em className="not-italic">{purityName}</em> : null} ({purityPct}%)
                                </span>
                              </div>
                              {e.reference ? <div className="text-slate-500 text-[11px]">{e.reference}</div> : null}
                              {(e.issue_memo_no || e.return_memo_no) && (
                                <div className="mt-0.5 inline-flex gap-1 flex-wrap items-center">
                                  {e.issue_memo_no && <MemoLink id={e.goldsmith_issue_id} memoNo={e.issue_memo_no} testId={`gold-ledger-merged-memo-${e.id}`} />}
                                  {e.return_memo_no && <ReturnMemoLink id={e.return_id} memoNo={e.return_memo_no} testId={`gold-ledger-merged-return-memo-${e.id}`} />}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{formatGrams(convertG999(e.pure_grams_999, purity))}</td>
                            {showRateCol && <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{formatINR(convertRate(e.rate_per_gram_999, purity))}</td>}
                            {showValueCol && <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{formatINR(e.value)}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-amber-200 bg-amber-50/60 font-semibold text-[var(--ink-navy)]">
                        <td className="px-2 py-2" colSpan={2}>Total ({g.entries.length} entries)</td>
                        <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="gold-ledger-merged-total-grams">{totalG}</td>
                        {showRateCol && <td className="px-2 py-2 text-right font-mono tabular-nums">{formatINR(derivedRate(g.total_value, g.total_pure_grams_999, purity))}</td>}
                        {showValueCol && <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="gold-ledger-merged-total-value">{formatINR(g.total_value)}</td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMergedDetail(null)} data-testid="gold-ledger-merged-detail-close">Close</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
